"use client";

import { useState, useEffect } from "react";
import { Loader2, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

interface Area {
  id: string;
  name: string;
  pincodes: string[];
  pickupFee: number;
  deliveryFee: number;
  returnFee: number;
  slaCutoffTime: string;
  serviceable: boolean;
  active: boolean;
}

interface AreaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  area?: Area | null;
  onSuccess: () => void;
}

export function AreaFormDialog({
  open,
  onOpenChange,
  area,
  onSuccess,
}: AreaFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [pincodes, setPincodes] = useState<string[]>([]);
  const [pincodeInput, setPincodeInput] = useState("");
  const [pickupFee, setPickupFee] = useState("0");
  const [deliveryFee, setDeliveryFee] = useState("0");
  const [returnFee, setReturnFee] = useState("0");
  const [slaCutoffTime, setSlaCutoffTime] = useState("14:00");
  const [serviceable, setServiceable] = useState(true);
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (area) {
      setName(area.name);
      setPincodes(area.pincodes);
      setPickupFee(area.pickupFee.toString());
      setDeliveryFee(area.deliveryFee.toString());
      setReturnFee(area.returnFee.toString());
      setSlaCutoffTime(area.slaCutoffTime);
      setServiceable(area.serviceable);
      setActive(area.active);
    } else {
      setName("");
      setPincodes([]);
      setPickupFee("0");
      setDeliveryFee("0");
      setReturnFee("0");
      setSlaCutoffTime("14:00");
      setServiceable(true);
      setActive(true);
    }
    setError(null);
    setPincodeInput("");
  }, [area, open]);

  const handleAddPincode = () => {
    const trimmed = pincodeInput.trim();
    if (trimmed && !pincodes.includes(trimmed)) {
      setPincodes([...pincodes, trimmed]);
      setPincodeInput("");
    }
  };

  const handleRemovePincode = (pincode: string) => {
    setPincodes(pincodes.filter((p) => p !== pincode));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddPincode();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const payload = {
        name,
        pincodes,
        pickupFee: parseFloat(pickupFee),
        deliveryFee: parseFloat(deliveryFee),
        returnFee: parseFloat(returnFee),
        slaCutoffTime,
        serviceable,
        active,
      };

      const url = area
        ? `/api/admin/areas/${area.id}`
        : "/api/admin/areas";

      const res = await fetch(url, {
        method: area ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to save area");
        return;
      }

      onSuccess();
      onOpenChange(false);
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{area ? "Edit Area" : "Create Area"}</DialogTitle>
          <DialogDescription>
            {area
              ? "Update the coverage area details."
              : "Add a new serviceable coverage area."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Area Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Koramangala"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Pincodes</Label>
            <div className="flex gap-2">
              <Input
                value={pincodeInput}
                onChange={(e) => setPincodeInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter pincode"
              />
              <Button type="button" variant="outline" onClick={handleAddPincode}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {pincodes.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {pincodes.map((pincode) => (
                  <Badge
                    key={pincode}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {pincode}
                    <button
                      type="button"
                      onClick={() => handleRemovePincode(pincode)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {pincodes.length === 0 && (
              <p className="text-xs text-slate-500">Add at least one pincode</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pickupFee">Pickup Fee</Label>
              <Input
                id="pickupFee"
                type="number"
                min="0"
                step="0.01"
                value={pickupFee}
                onChange={(e) => setPickupFee(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deliveryFee">Delivery Fee</Label>
              <Input
                id="deliveryFee"
                type="number"
                min="0"
                step="0.01"
                value={deliveryFee}
                onChange={(e) => setDeliveryFee(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="returnFee">Return Fee</Label>
              <Input
                id="returnFee"
                type="number"
                min="0"
                step="0.01"
                value={returnFee}
                onChange={(e) => setReturnFee(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="slaCutoffTime">SLA Cutoff Time</Label>
            <Input
              id="slaCutoffTime"
              type="time"
              value={slaCutoffTime}
              onChange={(e) => setSlaCutoffTime(e.target.value)}
            />
            <p className="text-xs text-slate-500">
              Orders placed before this time are eligible for same-day service
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="serviceable">Serviceable</Label>
              <p className="text-xs text-slate-500">
                Accept new bookings in this area
              </p>
            </div>
            <Switch
              id="serviceable"
              checked={serviceable}
              onCheckedChange={setServiceable}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="active">Active</Label>
              <p className="text-xs text-slate-500">
                Show this area in the system
              </p>
            </div>
            <Switch
              id="active"
              checked={active}
              onCheckedChange={setActive}
            />
          </div>

          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || pincodes.length === 0}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : area ? (
                "Update Area"
              ) : (
                "Create Area"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
